import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Send, Search, Users, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { useCanSendNotifications } from '@/lib/permissions/useCanAccess';
import { useHasFeature } from '@/lib/permissions/useCompanyFeatures';
import { PaywallOverlay } from '@/components/ui/PaywallOverlay';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import {
  useCompose,
  useNotificationCandidates,
  type CandidateUser,
} from './api';

const TITLE_MAX = 200;
const BODY_MAX = 2000;
const EMOJI_QUICK = ['🎉', '🚀', '✅', '⚠️', '📣', '🔔', '🙏', '💡'];

export function ComposeNotificationPage() {
  const t = useT();
  const navigate = useNavigate();
  const canSend = useCanSendNotifications();
  const hasFeature = useHasFeature('notifications');
  useFavoritable(t('notifications.compose.pageTitle'));

  const candidatesQ = useNotificationCandidates();
  const compose = useCompose();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [filter, setFilter] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const items = candidatesQ.data ?? [];
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((u) => matches(u, q));
  }, [items, filter]);

  if (!canSend) {
    return <Navigate to="/" replace />;
  }

  if (!hasFeature) {
    return (
      <div className="page space-y-6">
        <header>
          <h1
            className="text-2xl font-black tracking-tight text-[var(--color-xk-lavender)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {t('notifications.compose.pageTitle')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-xk-text-muted)]">
            {t('notifications.compose.pageSubtitle')}
          </p>
        </header>
        <PaywallOverlay
          title={t('paywall.notifications.title')}
          description={t('paywall.notifications.description')}
          perks={[
            t('paywall.notifications.perk1'),
            t('paywall.notifications.perk2'),
            t('paywall.notifications.perk3'),
          ]}
        >
          <ComposeNotificationPreview />
        </PaywallOverlay>
      </div>
    );
  }

  function toggle(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function selectAll() {
    setSelected(new Set(filtered.map((u) => u.id)));
  }

  function clearSelection() {
    setSelected(new Set());
  }

  function submit() {
    if (!title.trim()) {
      toast({ tone: 'error', title: t('notifications.compose.titleRequired') });
      return;
    }
    if (!body.trim()) {
      toast({ tone: 'error', title: t('notifications.compose.bodyRequired') });
      return;
    }
    if (selected.size === 0) {
      toast({ tone: 'error', title: t('notifications.compose.recipientsRequired') });
      return;
    }
    compose.mutate(
      {
        title: title.trim(),
        body: body.trim(),
        recipientUserIds: [...selected],
        sendEmail,
      },
      {
        onSuccess: () => {
          toast({
            tone: 'success',
            title: t('notifications.toast.sent', { count: selected.size }),
          });
          setTitle('');
          setBody('');
          setSelected(new Set());
          navigate(-1);
        },
        onError: (e: Error) => toast({ tone: 'error', title: e.message }),
      },
    );
  }

  return (
    <div className="page space-y-6">
      <header>
        <h1
          className="text-2xl font-black tracking-tight text-[var(--color-xk-lavender)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {t('notifications.compose.pageTitle')}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-xk-text-muted)]">
          {t('notifications.compose.pageSubtitle')}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,400px)]">
        {/* Compose */}
        <Card>
          <CardHeader>
            <CardTitle>{t('notifications.compose.messageTitle')}</CardTitle>
            <CardDescription>{t('notifications.compose.messageDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="block space-y-1.5">
              <span className="text-xs font-semibold text-[var(--color-fg-muted)]">
                {t('notifications.compose.titleLabel')}
              </span>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
                placeholder={t('notifications.compose.titlePlaceholder')}
              />
              <span className="text-[10px] text-[var(--color-fg-subtle)]">
                {title.length}/{TITLE_MAX}
              </span>
            </label>

            <label className="block space-y-1.5">
              <span className="text-xs font-semibold text-[var(--color-fg-muted)]">
                {t('notifications.compose.bodyLabel')}
              </span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
                rows={6}
                placeholder={t('notifications.compose.bodyPlaceholder')}
                className="w-full resize-y rounded-lg border border-[var(--color-border-default)] bg-[rgb(0_0_0/0.4)] px-3 py-2 text-sm text-[var(--color-fg-primary)] placeholder:text-[var(--color-fg-subtle)] focus:border-[var(--color-accent-ink)] focus:outline-none"
              />
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {EMOJI_QUICK.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setBody((b) => `${b}${e}`)}
                      className="grid h-7 w-7 place-items-center rounded-md text-base hover:bg-[var(--color-bg-elevated)]"
                    >
                      {e}
                    </button>
                  ))}
                </div>
                <span className="text-[10px] text-[var(--color-fg-subtle)]">
                  {body.length}/{BODY_MAX}
                </span>
              </div>
            </label>

            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="h-3.5 w-3.5 accent-[var(--color-accent)]"
              />
              <Mail className="h-3.5 w-3.5 text-[var(--color-xk-text-muted)]" />
              <span className="text-xs text-[var(--color-xk-lavender)]">
                {t('notifications.compose.emailCopy')}
              </span>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={submit}
                loading={compose.isPending}
                leftIcon={<Send className="h-3.5 w-3.5" />}
              >
                {t('notifications.compose.send', { count: selected.size })}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recipients */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              {t('notifications.compose.recipientsTitle')}
              <Badge tone="neutral">{selected.size}</Badge>
            </CardTitle>
            <CardDescription>{t('notifications.compose.recipientsDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[var(--color-fg-subtle)]" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t('notifications.compose.searchPlaceholder')}
                className="h-9 pl-9"
              />
            </div>

            <div className="flex items-center justify-between text-[10px] text-[var(--color-fg-subtle)]">
              <span>
                {filtered.length} {t('notifications.compose.available')}
              </span>
              <div className="flex gap-2">
                <button type="button" onClick={selectAll} className="hover:text-[var(--color-accent-ink)]">
                  {t('common.selectAll')}
                </button>
                <span>·</span>
                <button type="button" onClick={clearSelection} className="hover:text-[var(--color-accent-ink)]">
                  {t('common.clear')}
                </button>
              </div>
            </div>

            <div className="max-h-[420px] overflow-y-auto rounded-lg border border-[var(--color-border-default)] scrollbar-thin">
              {candidatesQ.isLoading && (
                <div className="p-3 space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 rounded" />
                  ))}
                </div>
              )}
              {!candidatesQ.isLoading && filtered.length === 0 && (
                <p className="px-3 py-6 text-center text-[12px] text-[var(--color-fg-muted)]">
                  {t('notifications.compose.noCandidates')}
                </p>
              )}
              <ul className="divide-y divide-[var(--color-border-subtle)]">
                {filtered.map((u) => {
                  const checked = selected.has(u.id);
                  return (
                    <li key={u.id}>
                      <label
                        className={cn(
                          'flex cursor-pointer items-center gap-2.5 px-3 py-2 hover:bg-[var(--color-bg-elevated)]',
                          checked && 'bg-[color-mix(in_srgb,var(--color-xk-accent)_06%,transparent)]',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(u.id)}
                          className="h-3.5 w-3.5 accent-[var(--color-accent)]"
                        />
                        <span
                          aria-hidden="true"
                          className="grid h-7 w-7 flex-none place-items-center rounded-full bg-[var(--color-bg-elevated)] text-[10px] font-extrabold text-[var(--color-xk-lavender)]"
                        >
                          {(u.firstname?.[0] ?? u.email[0] ?? '?').toUpperCase()}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[12px] font-bold text-[var(--color-xk-lavender)]">
                            {`${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || u.email}
                          </div>
                          <div className="truncate text-[10px] text-[var(--color-xk-text-muted)]">
                            {u.email}
                          </div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function matches(u: CandidateUser, q: string): boolean {
  const haystack = `${u.firstname ?? ''} ${u.lastname ?? ''} ${u.email}`.toLowerCase();
  return haystack.includes(q);
}

// ─── Paywall preview ────────────────────────────────────────────────────────
// Static sample of the compose surface, shown blurred behind the paywall.
function ComposeNotificationPreview() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,400px)]">
      <Card>
        <CardHeader>
          <CardTitle>Mensaje</CardTitle>
          <CardDescription>Componer una notificación</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold text-[var(--color-fg-muted)]">
              Título
            </span>
            <Input value="¡Reabastecimiento listo!" readOnly />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold text-[var(--color-fg-muted)]">
              Cuerpo
            </span>
            <textarea
              value={
                'Equipo, hay nueva mercancía disponible en la estación 4. Por favor confirmen recepción.'
              }
              readOnly
              rows={5}
              className="w-full resize-y rounded-lg border border-[var(--color-border-default)] bg-[rgb(0_0_0/0.4)] px-3 py-2 text-sm text-[var(--color-fg-primary)]"
            />
          </label>
          <div className="flex justify-end">
            <Button leftIcon={<Send className="h-3.5 w-3.5" />}>Enviar a 3</Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Destinatarios
            <Badge tone="neutral">3</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-[var(--color-border-subtle)]">
            {[
              { name: 'María González', email: 'maria@acme.com' },
              { name: 'Carlos Pérez', email: 'carlos@acme.com' },
              { name: 'Sofía Ramírez', email: 'sofia@acme.com' },
            ].map((u) => (
              <li
                key={u.email}
                className="flex items-center gap-2.5 px-3 py-2"
              >
                <span className="grid h-7 w-7 place-items-center rounded-full bg-[var(--color-bg-elevated)] text-[10px] font-extrabold text-[var(--color-xk-lavender)]">
                  {u.name[0]}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12px] font-bold text-[var(--color-xk-lavender)]">
                    {u.name}
                  </div>
                  <div className="truncate text-[10px] text-[var(--color-xk-text-muted)]">
                    {u.email}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
